import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { AppDispatch, RootState } from '../../data/store';
import { fetchCategoriesThunk } from '../../data/thunks/projectDataThunk';
import Table from '../common/Table';
import { ProjectDataCategory } from '../../data/models/projectData';

interface TableRow {
	id: string;
	dataCategory: string;
	description: string;
	associatedProjectTypes: number;
	numberOfDataPoints: number;
	hierarchy: string[];
	isCategory: boolean;
}

export default function ProjectSettingsDataScreen() {
	const dispatch = useDispatch<AppDispatch>();
	const { categories, loading } = useSelector(
		(state: RootState) => state.projectData
	);

	const [sortModel, setSortModel] = useState<GridSortModel>([]);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(25);

	useEffect(() => {
		dispatch(fetchCategoriesThunk({}));
	}, [dispatch]);

	const transformedRows = useMemo((): TableRow[] => {
		if (!categories || categories.length === 0) return [];

		const rows: TableRow[] = [];

		categories.forEach((category: ProjectDataCategory) => {
			// Add the category row
			rows.push({
				id: category.id,
				dataCategory: category.name,
				description: category.description || '',
				associatedProjectTypes: 16, // TODO: Get actual count from data
				numberOfDataPoints:
					category.projectDataPointDefinitions?.length || 0,
				hierarchy: [category.name],
				isCategory: true,
			});

			// Add data point rows as children
			if (
				category.projectDataPointDefinitions &&
				category.projectDataPointDefinitions.length > 0
			) {
				[...category.projectDataPointDefinitions]
					.sort((a, b) => a.displayOrder - b.displayOrder)
					.forEach((definition) => {
						const displayName = definition.label || definition.name;
						rows.push({
							id: `${category.id}-${definition.id}`,
							dataCategory: displayName,
							description: definition.description || '',
							associatedProjectTypes: 0,
							numberOfDataPoints: 0,
							hierarchy: [category.name, displayName],
							isCategory: false,
						});
					});
			}
		});

		return rows;
	}, [categories]);

	const columns: GridColDef[] = [
		{
			field: 'description',
			headerName: 'Description',
			flex: 2,
			minWidth: 300,
		},
		{
			field: 'associatedProjectTypes',
			headerName: 'Associated Project Types',
			width: 200,
			type: 'number',
			renderCell: (params) => {
				return params.row.isCategory ? params.value : '';
			},
		},
		{
			field: 'numberOfDataPoints',
			headerName: 'Number of Data Points',
			width: 180,
			type: 'number',
			renderCell: (params) => {
				return params.row.isCategory ? params.value : '';
			},
		},
	];

	const getTreeDataPath = (row: TableRow) => row.hierarchy;

	const groupingColDef = {
		headerName: 'Data Category',
		width: 500,
	};

	const getRowClassName = (params: any) => {
		return params.row.isCategory ? '' : 'row-is-child';
	};

	return (
		<Table
			columns={columns}
			rows={transformedRows}
			rowCount={categories.length}
			loading={loading}
			sortModel={sortModel}
			onSortModelChange={setSortModel}
			page={page}
			pageSize={pageSize}
			onPaginationModelChange={(model) => {
				setPage(model.page);
				setPageSize(model.pageSize);
			}}
			treeData={true}
			getTreeDataPath={getTreeDataPath}
			groupingColDef={groupingColDef}
			defaultGroupingExpansionDepth={0}
			getRowClassName={getRowClassName}
			hideFooterPagination={true}
			hideFooter={false}
			autoHeight={false}
			boxSx={{
				height: '100%',
				'& .MuiDataGrid-row--group .MuiDataGrid-cell': {
					fontWeight: 500,
				},
				'& .row-is-child .MuiDataGrid-treeDataGroupingCell::before': {
					content: '"{ }"',
					marginRight: '8px',
					fontSize: '15px',
					color: 'text.secondary',
					fontWeight: 'bold',
				},
			}}
		/>
	);
}
